import type { VisualizationBaseCondition } from "@models/VisualizationBase";

export type MetricResultType = "percentage" | "mean";

/**
 * Single grid splits into Value Focus and Variable Focus because each has a
 * distinct "represented categories" result: Value Focus produces one
 * percentage per grid variable, Variable Focus produces one percentage per
 * value for the chosen variable.
 */
export type MetricSourceKind =
  | "single_one_dimensional"
  | "multiple_one_dimensional"
  | "single_grid_value_focus"
  | "single_grid_variable_focus"
  | "numeric";

// Stable reference to the question a metric is built on. label/title are
// cached display fallbacks only — never read as authoritative; the metric's
// identity and calc-time behavior always resolve from questionId.
export interface MetricSourceQuestionRef {
  questionId: string;
  questionLabel: string;
  questionTitle: string;
}

// A named rollup of >=2 distinct values/variables from the same source item.
// Stores stable ids (not labels), since a saved Metric must survive
// relabeling — unlike WidgetRollup, which is recomputed per-query from
// ephemeral chart config.
export interface MetricRollup {
  id: string;
  name: string;
  calculationType: "SUM_SELECTED";
  sourceIds: string[];
}

// One-dimensional Single: all source categories (default, abstract — never
// enumerates ids, so newly-added categories automatically follow), one
// selected value, or a named rollup.
export type SingleFocusSelection =
  | { mode: "all" }
  | { mode: "one"; valueId: string; valueLabel: string }
  | { mode: "rollup"; rollup: MetricRollup };

// One-dimensional Multiple: each selected response variable becomes its own
// represented Percentage category at calc time.
export interface MultipleFocusSelection {
  variableIds: string[];
  variableLabels: Record<string, string>;
}

// Single grid, Value Focus: one grid value or a rollup of grid values,
// applied independently per grid variable at calc time.
export type GridValueFocusSelection =
  | { mode: "one"; valueId: string; valueLabel: string }
  | { mode: "rollup"; rollup: MetricRollup };

// Single grid, Variable Focus: one grid variable — result is one percentage
// per value for that variable.
export interface GridVariableFocusSelection {
  variableId: string;
  variableLabel: string;
}

// Number/Float: every variable on the source question becomes its own
// represented Mean category, using valid recorded numeric values only — the
// researcher doesn't choose a subset; reduction happens downstream (Scorecard
// brand matching, or hiding in other chart types).
export interface NumericFocusSelection {
  variableIds: string[];
  variableLabels: Record<string, string>;
}

export type MetricSource =
  | {
      kind: "single_one_dimensional";
      resultType: "percentage";
      question: MetricSourceQuestionRef;
      focus: SingleFocusSelection;
    }
  | {
      kind: "multiple_one_dimensional";
      resultType: "percentage";
      question: MetricSourceQuestionRef;
      focus: MultipleFocusSelection;
    }
  | {
      kind: "single_grid_value_focus";
      resultType: "percentage";
      question: MetricSourceQuestionRef;
      focus: GridValueFocusSelection;
    }
  | {
      kind: "single_grid_variable_focus";
      resultType: "percentage";
      question: MetricSourceQuestionRef;
      focus: GridVariableFocusSelection;
    }
  | {
      kind: "numeric";
      resultType: "mean";
      question: MetricSourceQuestionRef;
      focus: NumericFocusSelection;
    };

// Percentage offers a real population choice; Mean is fixed to valid-numeric
// respondents per PRD (still modeled explicitly so it round-trips like any
// other saved selection, rather than being purely derived at render time).
export type MetricPopulation =
  | {
      resultType: "percentage";
      mode: "total_survey_population" | "valid_question_respondents";
    }
  | { resultType: "mean"; mode: "valid_question_respondents" };

export function defaultMetricPopulationFor(
  resultType: MetricResultType,
): MetricPopulation {
  return resultType === "mean"
    ? { resultType: "mean", mode: "valid_question_respondents" }
    : { resultType: "percentage", mode: "valid_question_respondents" };
}

// Mirrors VisualizationBase's custom/preconfigured split, but Custom here is
// always inline/owned by the metric (never a toggle-off "no base" state —
// "no restriction" is its own explicit mode, all_eligible).
export type MetricBase =
  | { mode: "all_eligible" }
  | { mode: "custom"; name: string; conditions: VisualizationBaseCondition[] }
  | {
      mode: "preconfigured";
      preconfiguredBaseId: string;
      preconfiguredBaseName: string;
    };

export const DEFAULT_METRIC_BASE: MetricBase = { mode: "all_eligible" };

// One immutable snapshot appended to a metric's history on every save,
// including the resolved Base conditions at that moment.
export interface MetricHistoryEntry {
  id: string;
  savedAt: string;
  researcherEmail: string;
  researcherName?: string;
  snapshot: Omit<DatasetMetric, "history" | "deletedAt">;
}

// One "represented result" a saved metric produces — the unit a visualization
// maps onto one bar/brand/row. Shared across every visualization that consumes
// DatasetMetric (Scorecard rows, Bar Chart bars, ...); see
// dataset-metric-representation-utils.ts for the metric -> results adapter.
export interface MetricRepresentedResult {
  id: string;
  label: string;
  /** Every label variant this result could be echoed back as by the
   *  response/data endpoint — matching must try all of them, not just `label`. */
  matchLabels: string[];
}

// A Scorecard row referencing this metric — mirrors DatasetBase's
// WidgetBaseUsage shape/role, but a metric can be referenced by more than one
// row within the same widget (see dataset-metric-usage.ts), unlike a Base.
export interface MetricUsage {
  widgetId: string;
  dashboardId: string;
}

export interface DatasetMetric {
  /** Stable identifier — survives rename and edits; never derived from the name. */
  id: string;
  name: string;
  description?: string;
  /**
   * null only pre-save on a fresh, not-yet-configured metric (no question
   * chosen yet) — a deliberate deviation from DatasetBase's always-fully-typed
   * pattern, since a Metric has no natural "blank but valid" source.
   */
  source: MetricSource | null;
  population: MetricPopulation;
  base: MetricBase;
  createdAt: string;
  updatedAt: string;
  /**
   * Soft-delete marker. Present => excluded from the catalog list and from
   * name-uniqueness checks against active metrics, but the record (and its
   * history) stays in the same metricsConfigJson array so history and audit
   * trail survive deletion, per PRD.
   */
  deletedAt?: string;
  /** Append-only; newest last. Every create/edit save pushes one entry. */
  history: MetricHistoryEntry[];
  /** Saved Scorecard rows currently referencing this metric — a maintained
   *  index kept in sync by dataset-metric-usage-sync.ts whenever a widget is
   *  saved/duplicated/moved/deleted, not computed live. Absent/empty = unused. */
  usedByWidgets?: MetricUsage[];
}

export function createBlankMetric(): DatasetMetric {
  const now = new Date().toISOString();
  return {
    id: crypto.randomUUID(),
    name: "",
    description: "",
    source: null,
    population: defaultMetricPopulationFor("percentage"),
    base: DEFAULT_METRIC_BASE,
    createdAt: now,
    updatedAt: now,
    history: [],
  };
}

/** Case- and whitespace-insensitive key used to enforce unique metric names within a dataset. */
export function normalizeMetricName(name: string): string {
  return name.trim().toLowerCase();
}

/** Active (non-deleted) metrics — what the catalog list and name-uniqueness checks use. */
export function getActiveMetrics(metrics: DatasetMetric[]): DatasetMetric[] {
  return metrics.filter((metric) => !metric.deletedAt);
}

// Tolerant of both the array form (what we write) and a legacy { metrics: [] }
// wrapper, so an older or backend-shaped blob still reads cleanly.
export function parseMetricsConfigJson(json?: string | null): DatasetMetric[] {
  if (!json) return [];
  try {
    const parsed = JSON.parse(json);
    if (Array.isArray(parsed)) return parsed as DatasetMetric[];
    if (parsed && Array.isArray(parsed.metrics)) {
      return parsed.metrics as DatasetMetric[];
    }
    return [];
  } catch {
    console.warn("Failed to parse metricsConfigJson");
    return [];
  }
}

export function serializeMetricsConfigJson(metrics: DatasetMetric[]): string {
  return JSON.stringify(metrics);
}
